import {
  Autocomplete,
  Checkbox,
  Input as MuiInput,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import styled from '@/styles';
import ValueSlider from '@/components/Slider/ValueSlider';
import type {
  DayBucket,
  DependabilityV2Tier,
  LastActiveBucket,
} from '@/services/workersV2API';
import {
  LAST_ACTIVE_OPTIONS,
  Option,
  PROGRESS_OPTIONS,
  RADIUS,
  STATUS_OPTIONS,
  SUSPENSION_OPTIONS,
  SuspensionFilter,
  TAX_TYPE_OPTIONS,
  TIER_OPTIONS,
} from '../mappings';

export type GravyWorkersFilters = {
  tax_type?: '1099' | 'w2';
  skill_ids?: number[];
  dependability_v2_tier?: DependabilityV2Tier;
  last_active?: LastActiveBucket;
  active?: 'true' | 'false';
  accountability_status?: SuspensionFilter;
  progress?: DayBucket;
  zip?: string;
  radius: number;
};

export const EMPTY_FILTERS: GravyWorkersFilters = { radius: RADIUS.default };

const TEXT = '#3D3D3D';
const PLACEHOLDER = '#8A8A8A';
const CHEVRON = '#5A5A5A';
const CHEVRON_SIZE = 16;
const SKILLS_PANEL_MIN_WIDTH = 260;

const Panel = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(8, minmax(max-content, 1fr)) 140px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  },
  '@media (max-width: 768px)': { gridTemplateColumns: '1fr 1fr' },
});

const Cell = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: 0,
  minHeight: '48px',
  borderRight: '1px solid #DADADA',
  padding: '4px 6px',
  '&:last-child': { borderRight: 'none' },
});

// GRAVY-10834: every dropdown panel on the row is the design's panel (Figma
// "GravyWork 2.0 Baseline" 38014:14314): white, 1px #DADADA, 8px radius, a
// soft shadow, 44px rows divided by 1px lines. Native <select> menus are drawn
// by the browser and can never look like this, so the five single-value
// filters are MUI Selects and share these styles with the Skills picker.
const PANEL_SX = {
  marginTop: '6px',
  border: '1px solid #DADADA',
  borderRadius: '8px',
  boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.12)',
  '& .MuiList-root, & .MuiAutocomplete-listbox': { padding: 0 },
};
// `height`, not `minHeight`: MuiMenuItem resets min-height to `auto` above
// the sm breakpoint in a media rule that outranks the sx value.
const OPTION_SX = {
  height: '44px',
  boxSizing: 'border-box',
  padding: '0 16px',
  fontFamily: 'inherit',
  fontSize: '14px',
  color: '#1C1C1C',
  borderBottom: '1px solid #DADADA',
  '&:last-child': { borderBottom: 'none' },
  '&.Mui-selected, &.Mui-selected:hover, &[aria-selected="true"]': {
    backgroundColor: '#F5F5F5',
  },
};
const OPTION_SX_INLINE = {
  height: '44px',
  boxSizing: 'border-box' as const,
  padding: '0 16px 0 8px',
  fontFamily: 'inherit',
  fontSize: '14px',
  color: '#1C1C1C',
  borderBottom: '1px solid #DADADA',
};
const MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { sx: PANEL_SX },
} as const;

const Placeholder = styled('span', { color: PLACEHOLDER });

const Chevron = (props: { className?: string }) => (
  <KeyboardArrowDownIcon
    {...props}
    sx={{ fontSize: CHEVRON_SIZE, color: CHEVRON, right: '2px' }}
  />
);

const Input = styled('input', {
  width: '100%',
  height: '36px',
  padding: '0 2px',
  border: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '36px',
  color: TEXT,
  '&::placeholder': { color: PLACEHOLDER, opacity: 1 },
});

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const SkillsSummary = styled('span', {
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '14px',
  color: TEXT,
});

const RadiusLabel = styled('div', {
  fontSize: '11px',
  textAlign: 'right',
  color: TEXT,
});

type Props = {
  filters: GravyWorkersFilters;
  onChange: (next: GravyWorkersFilters) => void;
  skills: Array<{ id: number; name: string }>;
};

const SingleSelect = <T extends string>({
  testId,
  label,
  value,
  options,
  onPick,
}: {
  testId: string;
  label: string;
  value: T | undefined;
  options: Option<T>[];
  onPick: (v: T | undefined) => void;
}) => (
  <Cell data-testid={testId}>
    <Select
      variant="standard"
      displayEmpty
      value={value ?? ''}
      onChange={(e) =>
        onPick((String(e.target.value) || undefined) as T | undefined)
      }
      input={<MuiInput disableUnderline />}
      inputProps={{ 'aria-label': label }}
      IconComponent={Chevron}
      MenuProps={MENU_PROPS}
      renderValue={(v) =>
        v ? (
          options.find((o) => o.value === v)?.label
        ) : (
          <Placeholder data-testid={`${testId}-placeholder`}>
            {label}
          </Placeholder>
        )
      }
      sx={{
        width: '100%',
        height: '36px',
        fontFamily: 'inherit',
        fontSize: '14px',
        color: TEXT,
        '& .MuiSelect-select': {
          padding: `0 ${CHEVRON_SIZE + 6}px 0 2px !important`,
          minHeight: 0,
          lineHeight: '36px',
        },
      }}
    >
      <MenuItem value="" sx={{ ...OPTION_SX, color: PLACEHOLDER }}>
        {label}
      </MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={OPTION_SX}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  </Cell>
);

const FilterRow = ({ filters, onChange, skills }: Props) => {
  const set = <K extends keyof GravyWorkersFilters>(
    key: K,
    value: GravyWorkersFilters[K],
  ) => {
    const next = { ...filters, [key]: value };
    if (
      value === undefined ||
      (Array.isArray(value) && value.length === 0) ||
      value === ''
    )
      delete next[key];
    onChange(next);
  };

  const selectedSkills = skills.filter((s) =>
    (filters.skill_ids ?? []).includes(s.id),
  );
  const skillsSummary = selectedSkills.map((s) => s.name).join(', ');

  return (
    <Panel data-testid="advFilterPanel">
      <SingleSelect
        testId="adv-drop-lastActive"
        label="Last active"
        value={filters.last_active}
        options={LAST_ACTIVE_OPTIONS}
        onPick={(v) => set('last_active', v)}
      />
      <SingleSelect
        testId="adv-drop-progress"
        label="Progress"
        value={filters.progress}
        options={PROGRESS_OPTIONS}
        onPick={(v) => set('progress', v)}
      />
      <SingleSelect
        testId="adv-drop-status"
        label="Account status"
        value={filters.active}
        options={STATUS_OPTIONS}
        onPick={(v) => set('active', v)}
      />
      <SingleSelect
        testId="adv-drop-suspension"
        label="Suspension"
        value={filters.accountability_status}
        options={SUSPENSION_OPTIONS}
        onPick={(v) => set('accountability_status', v)}
      />
      <Cell data-testid="adv-drop-skills">
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          popupIcon={
            <KeyboardArrowDownIcon
              sx={{ fontSize: CHEVRON_SIZE, color: CHEVRON }}
            />
          }
          options={skills}
          slotProps={{
            popper: {
              placement: 'bottom-start',
              style: { width: 'auto', minWidth: SKILLS_PANEL_MIN_WIDTH },
            },
            paper: { sx: PANEL_SX },
          }}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={selectedSkills}
          onChange={(_, newValue) =>
            set(
              'skill_ids',
              newValue.map((v) => v.id),
            )
          }
          renderTags={(value) =>
            value.length ? (
              <SkillsSummary
                data-testid="adv-drop-skills-summary"
                title={skillsSummary}
              >
                Skills ({value.length})
              </SkillsSummary>
            ) : null
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...rest } = props as typeof props & { key: string };
            return (
              <li key={key} {...rest} style={OPTION_SX_INLINE}>
                <Checkbox
                  icon={uncheckedIcon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={selectedSkills.length ? '' : 'Skills'}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'adv-drop-skills-input',
                'aria-label': 'Skills',
              }}
              InputProps={{ ...params.InputProps, disableUnderline: true }}
            />
          )}
          sx={{
            '& .MuiInputBase-root': {
              height: '36px',
              fontFamily: 'inherit',
              fontSize: '14px',
              color: TEXT,
              flexWrap: 'nowrap',
            },
            '& .MuiAutocomplete-input::placeholder': {
              color: PLACEHOLDER,
              opacity: 1,
            },
            '& .MuiAutocomplete-popupIndicator': { padding: '2px' },
            '& .MuiAutocomplete-input': {
              padding: '0 !important',
              minWidth: '24px !important',
              ...(selectedSkills.length ? { flexGrow: 0, width: '24px' } : {}),
            },
          }}
        />
      </Cell>
      <SingleSelect
        testId="adv-drop-dep"
        label="Dependability"
        value={filters.dependability_v2_tier}
        options={TIER_OPTIONS}
        onPick={(v) => set('dependability_v2_tier', v)}
      />
      <SingleSelect
        testId="adv-drop-tax"
        label="Tax type"
        value={filters.tax_type}
        options={TAX_TYPE_OPTIONS}
        onPick={(v) => set('tax_type', v)}
      />
      <Cell>
        <Input
          data-testid="zipInput"
          aria-label="Zip code"
          placeholder="Zip code"
          inputMode="numeric"
          maxLength={5}
          size={8}
          value={filters.zip ?? ''}
          onChange={(e) =>
            set('zip', e.target.value.replace(/\D/g, '') || undefined)
          }
        />
      </Cell>
      <Cell>
        <RadiusLabel data-testid="radiusLabel">
          {filters.radius}mi radius
        </RadiusLabel>
        <div data-testid="radiusSlider">
          <ValueSlider
            id="gravyworkers-radius"
            ariaLabel="Distance radius in miles"
            ariaValueText={`${filters.radius} miles`}
            min={RADIUS.min}
            max={RADIUS.max}
            step={1}
            value={filters.radius}
            onChange={(v) => set('radius', v)}
          />
        </div>
      </Cell>
    </Panel>
  );
};

export default FilterRow;
